import { HeaderUserMenuItem } from '@/components/Header/navigation';
import { languages as supportedLanguages } from '@/config';
import { UserRole } from '@/models/User';
import { LogoutPath } from '@/modules/auth/paths';
import { CartPath } from '@/modules/store/paths';
import { classNames } from '@/utils/style/classNames';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import {
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
  LanguageIcon,
  ShoppingCartIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface MobileMenuBottomNavigationProps {
  role?: UserRole;
  showCartLink: boolean;
  close: () => void;
  userMenu: HeaderUserMenuItem[];
}

const MobileMenuBottomNavigation = ({
  role,
  showCartLink,
  close,
  userMenu,
}: MobileMenuBottomNavigationProps) => {
  const withClose = (fn?: () => void) => () => {
    fn?.();
    close();
  };

  return (
    <div className="py-6">
      {showCartLink && (
        <Link
          to={CartPath}
          onClick={close}
          className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
        >
          <div className="flex items-center gap-2">
            <ShoppingCartIcon className="h-8 w-8 text-black" />
            <span>Cart</span>
          </div>
        </Link>
      )}
      <Disclosure as="div" className="-mx-3">
        {({ open }) => (
          <>
            <DisclosureButton className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
              <div className="flex items-center gap-2">
                <UserCircleIcon className="h-8 w-8 text-black" />
                <span>Account</span>
              </div>
              <ChevronDownIcon
                className={classNames(open ? 'rotate-180' : '', 'h-5 w-5 flex-none')}
                aria-hidden="true"
              />
            </DisclosureButton>
            <DisclosurePanel className="mt-2 space-y-2">
              {userMenu
                .filter((item) => !item.role || item.role === role)
                .map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={withClose(item.onClick)}
                    className="rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-900 hover:bg-gray-50 flex items-center"
                  >
                    <span>{item.name}</span>
                  </Link>
                ))}
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
      <Disclosure as="div" className="-mx-3">
        {({ open }) => (
          <>
            <DisclosureButton className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
              <div className="flex items-center gap-2">
                <LanguageIcon className="h-8 w-8 text-black" />
                <span>Language</span>
              </div>
              <ChevronDownIcon
                className={classNames(open ? 'rotate-180' : '', 'h-5 w-5 flex-none')}
                aria-hidden="true"
              />
            </DisclosureButton>
            <DisclosurePanel className="mt-2 space-y-2">
              {supportedLanguages.map((item) => {
                const disabled = item.isSupported === false;

                return (
                  <DisclosureButton
                    key={item.name}
                    as="button"
                    type="button"
                    disabled={disabled}
                    className={classNames(
                      disabled
                        ? 'cursor-not-allowed text-gray-400 opacity-60'
                        : 'text-gray-900 hover:bg-gray-50',
                      'flex w-full items-center rounded-lg py-2 pl-6 pr-3 text-left text-sm font-semibold leading-7',
                    )}
                  >
                    <img
                      className="h-6 w-6 object-cover rounded-full mr-2"
                      src={item.flag}
                      alt=""
                    />
                    {item.name}
                  </DisclosureButton>
                );
              })}
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
      <Link
        to={LogoutPath}
        className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <ArrowRightStartOnRectangleIcon className="h-8 w-8 text-black" />
          <span>Sign out</span>
        </div>
      </Link>
    </div>
  );
};

export default MobileMenuBottomNavigation;
